import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ExecutionFilters } from '../components/executions/ExecutionFilters';
import { ExecutionsTable } from '../components/tables/ExecutionsTable';
import { useExecutionFilters } from '../hooks/useExecutionFilters';
import { flowsApi } from '../lib/api-client';

/**
 * Main ExecutionList component for displaying workflow execution history
 *
 * Updated to use the new DataTable system with:
 * - Server-side pagination
 * - Built-in search functionality
 * - Consistent UX with other tables
 * - Loading states and error handling
 * - Responsive design and dark mode support
 * - URL parameter support for filtering by workflow
 */
export function ExecutionList() {
	const [searchParams] = useSearchParams();
	const [workflowName, setWorkflowName] = useState<string>('');
	const [isRefreshing, setIsRefreshing] = useState(false);
	const refreshFunctionRef = useRef<(() => Promise<void>) | null>(null);

	// Custom hook for filter management (keeping existing filter logic)
	const {
		filters,
		handleSearchChange,
		handleStatusChange,
		handleFlowFilterChange,
		handleDateRangeChange,
		toggleFilters,
		resetFilters,
	} = useExecutionFilters();

	// Handle URL parameters for filtering
	useEffect(() => {
		const flowIdFromUrl = searchParams.get('flowId');

		if (flowIdFromUrl) {
			// Always update the filter when URL parameter changes, regardless of current state
			handleFlowFilterChange(flowIdFromUrl);

			// Fetch workflow name for display
			flowsApi
				.getFlow(flowIdFromUrl)
				.then(flow => setWorkflowName(flow.name))
				.catch(() => setWorkflowName(''));
		} else {
			// If no flowId in URL, clear the flow filter and reset workflow name
			handleFlowFilterChange('');
			setWorkflowName('');
		}
	}, [searchParams, handleFlowFilterChange]); // Remove filters.flowId dependency to avoid race conditions

	// Convert filters to ExecutionPaginationParams format for the DataTable
	const executionFilters = {
		search: filters.search || undefined,
		status: filters.status || undefined,
		flowId: filters.flowId || undefined,
		createdAfter: filters.dateRange.start || undefined,
		createdBefore: filters.dateRange.end || undefined,
	};

	// Determine title based on whether we're filtering by a specific workflow
	const title = workflowName ? `Executions for "${workflowName}"` : 'Execution History';
	const description = workflowName
		? `Monitor execution status and performance for this workflow`
		: 'Monitor workflow execution status and performance';

	// Handle refresh functionality
	const handleRefresh = async () => {
		if (refreshFunctionRef.current) {
			setIsRefreshing(true);
			try {
				await refreshFunctionRef.current();
			} catch (error) {
				console.error('Failed to refresh executions:', error);
			} finally {
				setIsRefreshing(false);
			}
		}
	};

	// Callback to receive refresh function from ExecutionsTable
	const handleRefreshFunctionReceived = (refreshFn: () => Promise<void>) => {
		refreshFunctionRef.current = refreshFn;
	};

	return (
		<div className="px-4 py-8 w-full sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex gap-4 items-center mb-4">
					<Link
						to="/workflows"
						className="inline-flex items-center text-gray-600 transition-colors dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
					>
						<ArrowLeft className="mr-1 w-5 h-5" />
						Back to Workflows
					</Link>
				</div>

				<div className="flex justify-between items-center mb-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
							{title}
						</h1>
						<p className="mt-1 text-gray-600 dark:text-gray-400">{description}</p>
					</div>

					{/* Refresh Button */}
					<button
						onClick={handleRefresh}
						disabled={isRefreshing}
						className="inline-flex items-center px-4 py-2 font-medium text-white bg-blue-600 rounded-lg transition-colors duration-200 hover:bg-blue-700 disabled:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
						title="Refresh execution history"
					>
						<RefreshCw
							className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
						/>
						Refresh
					</button>
				</div>

				{/* Filters Component */}
				<ExecutionFilters
					search={filters.search}
					status={filters.status}
					flowId={filters.flowId}
					dateRange={filters.dateRange}
					showFilters={filters.showFilters}
					onSearchChange={handleSearchChange}
					onStatusChange={handleStatusChange}
					onFlowIdChange={handleFlowFilterChange}
					onDateRangeChange={handleDateRangeChange}
					onToggleFilters={toggleFilters}
					onResetFilters={resetFilters}
				/>
			</div>

			{/* Executions Table with DataTable - Now includes pagination! */}
			<ExecutionsTable
				key={`executions-${filters.flowId || 'all'}`} // Force re-mount when flowId changes
				initialFilters={executionFilters}
				showSearch={false} // We're using the ExecutionFilters component for search
				className="bg-white rounded-lg border border-gray-200 shadow-sm transition-colors dark:bg-gray-800 dark:border-gray-700"
				onRefresh={handleRefreshFunctionReceived}
			/>
		</div>
	);
}
